import { ActionTree, ActionContext } from 'vuex';

import { contextifyClient } from '@/http-client';
import { camelToSnake, snakeToCamel } from '@/utils';

import { RootStateI, PaginationI } from '../state';
import { CourseI, CoursesStateI } from './state';

export const actions: ActionTree<CoursesStateI, RootStateI> = {
  async filter(
    context: ActionContext<CoursesStateI, RootStateI>,
    payload: PaginationI | null,
  ): Promise<void> {
    // convert the payload to url query params
    const params = new URLSearchParams();
    if (payload) {
      Object.entries(payload).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
        if (key === 'query') {
          params.append('title', value);
          params.append('description', value);
          // remove the query key
          params.delete('query');
        }
      });
    }
    const { data } = await contextifyClient.get(`/api/courses/filter?${params.toString()}`);
    context.commit('setCoursesResult', snakeToCamel(data));
    return data;
  },
  async add(
    context: ActionContext<CoursesStateI, RootStateI>,
    payload: CourseI,
  ): Promise<void> {
    const { data } = await contextifyClient.post('/api/courses/add', camelToSnake(payload));
    return data;
  },
  async get(
    context: ActionContext<CoursesStateI, RootStateI>,
    payload: number,
  ): Promise<void> {
    const { data } = await contextifyClient.get(`/api/courses/get/${payload}`);
    context.commit('setCourse', snakeToCamel(data));
    return data;
  },
  async update(
    context: ActionContext<CoursesStateI, RootStateI>,
    payload: CourseI,
  ): Promise<void> {
    const { data } = await contextifyClient.patch('/api/courses/update', camelToSnake(payload));
    return data;
  },
  async enroll(
    context: ActionContext<CoursesStateI, RootStateI>,
    payload: { courseId: number },
  ): Promise<void> {
    const { data } = await contextifyClient.post('/api/courses/enroll', camelToSnake(payload));
    return data;
  },

  async test(
    context: ActionContext<CoursesStateI, RootStateI>,
    text: string,
  ) {
    const payload = {
      appName: 'agent',
      userId: '1',
      sessionId: 's123',
      newMessage: {
        role: 'user',
        parts: [
          {
            // text: 'What is the capital of France? give me the answer
            //  in spanish with the most detailed explanation',
            text,
          },
        ],
      },
      streaming: true,
    };

    const response = await fetch('https://dg-contextify-290571698035.us-east1.run.app/run_sse', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    context.commit('clearResponse');

    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader!.read();
      if (done) break;

      // eslint-disable-next-line no-param-reassign
      buffer += decoder.decode(value, { stream: true });

      // Split into SSE messages
      const parts = buffer.split('\n\n');

      // Keep last partial chunk in buffer
      buffer = parts.pop() || '';

      // eslint-disable-next-line no-restricted-syntax
      for (const part of parts) {
        const line = part.trim();

        if (!line.startsWith('data:')) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const jsonStr = line.replace(/^data:\s*/, '');

        try {
          const json = JSON.parse(jsonStr);
          if (json.partial) {
            json.content.parts.forEach((p: any) => {
              context.commit('setResponse', p.text);
            });
          }
          console.log('json:', json);

          // Example: extract streamed text
          const txt = json?.content?.parts?.[0]?.text;
          if (txt) {
            console.log('text chunk:', txt);
          }
        } catch (err) {
          console.error('parse error:', err, jsonStr);
        }
      }
    }

    console.log('stream done');
  },

};
